import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getLiquidaciones, deleteLiquidacion, updateEstadoLiquidaciones } from '../../api/liquidaciones';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent, TextField, CircularProgress, TablePagination, Box, MenuItem, Select, InputLabel, FormControl, Grid, SelectChangeEvent } from '@mui/material';
import { Delete, Visibility, Update } from '@mui/icons-material';
import { estadoLiquidacion } from './types';
import EditLiquidacion from './_Edit';
import ViewLiquidacion from './_ViewById';
import { ConfirmDelete } from '../../components/ConfirmDelete';
import moment from 'moment';
import { useAuth } from '../../context/auth';
import { AccessDenied } from '../../components/AccessDenied/Index';
import { UseAlert } from '../../components/newAlert/NewAlert';

// Rango maximo de la consulta por fechas: el listado se carga completo en el cliente, asi que
// se acota para mantener la respuesta liviana. Debe coincidir con MaxMesesRango del backend
// (Application/Handlers/LiquidacionHandler/LiquidacionHandler.cs).
const MAX_MESES_RANGO = 2;
const FORMATO_FECHA = 'YYYY-MM-DD';

const validarRango = (inicio: string, fin: string): string | null => {
  const desde = moment(inicio, FORMATO_FECHA, true);
  const hasta = moment(fin, FORMATO_FECHA, true);
  if (!desde.isValid() || !hasta.isValid()) return 'Seleccione una fecha válida';
  if (hasta.isBefore(desde)) return 'La fecha fin no puede ser menor a la fecha inicio';
  if (hasta.isAfter(desde.clone().add(MAX_MESES_RANGO, 'months'))) return `El rango no puede ser mayor a ${MAX_MESES_RANGO} meses`;
  return null;
};

const Liquidaciones = () => {
  const [liquidaciones, setLiquidaciones] = useState<any[]>([]);
  const [openEdit, setOpenEdit] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [selectedLiquidacion, setSelectedLiquidacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [estadoFilter, setEstadoFilter] = useState<string[]>([]);
  // Rango por defecto: 1 mes atras y 1 mes adelante (2 meses en total). Antes llegaba solo
  // hasta hoy, y los servicios agendados a futuro no aparecian en el listado.
  const [startDate, setStartDate] = useState(() => moment().subtract(1, 'month').format(FORMATO_FECHA));
  const [endDate, setEndDate] = useState(() => moment().add(1, 'month').format(FORMATO_FECHA));
  const [numeroServicio, setNumeroServicio] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loadingEstado, setLoadingEstado] = useState<number | null>(null);

  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const ultimaConsulta = useRef(0);
  const primeraCarga = useRef(true);

  // UseAlert crea NewNotification en cada render: se guarda en un ref para no ponerlo como
  // dependencia de fetchLiquidaciones (si no, el efecto de consulta se dispararia sin parar).
  const { NewNotification } = UseAlert();
  const notificar = useRef(NewNotification);
  notificar.current = NewNotification;
  const { permisos } = useAuth();
  const { Prm_Liquid_Ver, Prm_Liquid_Eliminar, Prm_Liquid_Camb_Estado } = permisos;

  const buscandoPorServicio = numeroServicio !== '';
  const errorRango = useMemo(() => validarRango(startDate, endDate), [startDate, endDate]);
  const maxFechaFin = useMemo(() => {
    const desde = moment(startDate, FORMATO_FECHA, true);
    return desde.isValid() ? desde.add(MAX_MESES_RANGO, 'months').format(FORMATO_FECHA) : undefined;
  }, [startDate]);

  // Consulta al backend: por numero de servicio (ignora las fechas, para encontrar el servicio
  // sin importar cuando se agendo) o por el rango de fechas seleccionado.
  const fetchLiquidaciones = useCallback(async () => {
    if (!buscandoPorServicio && errorRango) return;

    // Si el usuario cambia los filtros mientras una consulta sigue en curso, solo se aplica la
    // respuesta de la ultima -- una respuesta vieja no pisa a la nueva.
    const consulta = ++ultimaConsulta.current;
    setLoading(true);
    const response = await getLiquidaciones(buscandoPorServicio ? { numero_servicio: numeroServicio } : { fechaInicio: startDate, fechaFin: endDate });
    if (consulta !== ultimaConsulta.current) return;

    if (response.isOk && response.data) {
      setLiquidaciones(response.data);
    } else {
      setLiquidaciones([]); // En caso de error o datos indefinidos, establecer un arreglo vacío
      if (response.message) notificar.current(response.message, 'warning');
    }
    setLoading(false);
  }, [buscandoPorServicio, numeroServicio, startDate, endDate, errorRango]);

  // Se vuelve a consultar cada vez que cambian las fechas o el numero de servicio. La primera
  // carga va inmediata; las siguientes esperan a que el usuario termine de escribir.
  useEffect(() => {
    setPage(0);
    if (primeraCarga.current) {
      primeraCarga.current = false;
      fetchLiquidaciones();
      return;
    }
    const timer = setTimeout(fetchLiquidaciones, 500);
    return () => clearTimeout(timer);
  }, [fetchLiquidaciones]);

  const handleOpenDeleteConfirm = useCallback((id: number) => {
    setDeleteId(id);
    setOpenConfirmDelete(true);
  }, []);

  const handleCloseDeleteConfirm = useCallback(() => {
    setOpenConfirmDelete(false);
    setDeleteId(null);
  }, []);

  const handleEstadoFilterChange = useCallback((e: SelectChangeEvent<string[]>) => {
    setEstadoFilter(e.target.value as string[]);
  }, []);

  // Al mover la fecha inicio, si la fecha fin queda por fuera del rango permitido se ajusta al
  // maximo (inicio + 2 meses) en lugar de bloquear la seleccion.
  const handleStartDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const nuevoInicio = e.target.value;
    setStartDate(nuevoInicio);
    setEndDate((finActual) => {
      const desde = moment(nuevoInicio, FORMATO_FECHA, true);
      if (!desde.isValid()) return finActual;
      const maximo = desde.clone().add(MAX_MESES_RANGO, 'months');
      const hasta = moment(finActual, FORMATO_FECHA, true);
      return !hasta.isValid() || hasta.isBefore(desde) || hasta.isAfter(maximo) ? maximo.format(FORMATO_FECHA) : finActual;
    });
  }, []);

  const handleEndDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
  }, []);

  const handleNumeroServicioChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setNumeroServicio(e.target.value.replace(/\D/g, ''));
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (deleteId !== null) {
      await deleteLiquidacion(deleteId);
      fetchLiquidaciones();
      handleCloseDeleteConfirm();
    }
  }, [deleteId, handleCloseDeleteConfirm, fetchLiquidaciones]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleEstadoChange = useCallback(async (id: number, newEstado: string) => {
    setLoadingEstado(id);
    const response = await updateEstadoLiquidaciones(id, parseInt(newEstado));
    setLoadingEstado(null);
    if (response.isOk) {
      setLiquidaciones((prevLiqui) => prevLiqui.map((liquidacion: any) => (liquidacion.SrvDI_Id === id ? { ...liquidacion, SrvDI_Estado: newEstado } : liquidacion)));
    }
  }, []);

  // El rango de fechas ya lo aplica el backend; aqui solo se filtra lo que ya se cargo.
  const termino = searchTerm.trim().toLowerCase();
  const filteredLiquidaciones = liquidaciones.filter((liquidacion: any) => {
    const matchesSearchTerm =
      termino === '' ||
      `${liquidacion.In_Nombre ?? ''} ${liquidacion.In_Apellido ?? ''}`.toLowerCase().includes(termino) ||
      (liquidacion.Srv_VehiculoPlacaOSerial ?? '').toLowerCase().includes(termino) ||
      String(liquidacion.SrvD_Srv_Id) === termino;
    const matchesEstadoFilter = estadoFilter.length === 0 || estadoFilter.includes(liquidacion.SrvDI_Estado.toString());

    return matchesSearchTerm && matchesEstadoFilter;
  });

  const getEstadoText = (id: string) => {
    const estado = estadoLiquidacion.find((e) => e.id.toString() === id);
    return estado ? estado.texto : 'Todos';
  };

  const calculateTotalValue = (liquidaciones: any[]) => {
    return liquidaciones.reduce((total, liquidacion) => {
      const valor = parseFloat(liquidacion.SrvDI_Valor) || 0;
      return total + valor;
    }, 0);
  };

  if (Prm_Liquid_Ver === 0) {
    return <AccessDenied />;
  }

  return (
    <div style={{ marginTop: '5px' }}>
      {/* Los filtros se mantienen visibles durante la carga: ahora cada cambio consulta al
          backend, y si se desmontaran el campo perderia el foco mientras el usuario escribe. */}
      <div style={{ marginTop: '5px' }}>
        <Grid container spacing={2} alignItems='center' mb={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label='Buscar' placeholder='Trabajador, placa o N° servicio' value={searchTerm} onChange={handleSearchChange} fullWidth size='small' />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField label='N° servicio' title='Trae las liquidaciones del servicio sin importar su fecha' value={numeroServicio} onChange={handleNumeroServicioChange} inputProps={{ inputMode: 'numeric' }} fullWidth size='small' />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl variant='outlined' size='small' fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                multiple
                value={estadoFilter}
                onChange={handleEstadoFilterChange}
                label='Estado'
                renderValue={(selected) => {
                  // Mostrar los nombres de los estados seleccionados
                  return selected.map((id) => getEstadoText(id)).join(', ');
                }}>
                {estadoLiquidacion.map((estado) => (
                  <MenuItem key={estado.id} value={estado.id.toString()}>
                    {estado.texto}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField label='Fecha inicio' type='date' value={startDate} onChange={handleStartDateChange} disabled={buscandoPorServicio} InputLabelProps={{ shrink: true }} fullWidth size='small' />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label='Fecha fin'
              type='date'
              value={endDate}
              onChange={handleEndDateChange}
              disabled={buscandoPorServicio}
              error={!buscandoPorServicio && !!errorRango}
              helperText={!buscandoPorServicio && errorRango}
              inputProps={{ min: startDate, max: maxFechaFin }}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size='small'
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <IconButton onClick={fetchLiquidaciones} title='Actualizar' className='tool-btn'>
              <Update />
            </IconButton>
          </Grid>
        </Grid>
        {loading ? (
          <Box display='flex' justifyContent='center' alignItems='center' py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Servicio</TableCell>
                    <TableCell>Trabajador</TableCell>
                    <TableCell>Fecha de servicio</TableCell>
                    <TableCell>Fecha de finalizacion</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Fecha de liquidacion</TableCell>
                    <TableCell>Placa/Serie</TableCell>
                    <TableCell>Detalle</TableCell>
                    <TableCell>Valor</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLiquidaciones.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((liquidacion: any) => (
                    <TableRow key={liquidacion.SrvDI_Id}>
                      <TableCell>{liquidacion.SrvD_Srv_Id}</TableCell>
                      <TableCell>
                        {liquidacion.In_Nombre} {liquidacion.In_Apellido}
                      </TableCell>
                      <TableCell>{moment(liquidacion.Srv_FechaServicio).format('YYYY-MM-DD')}</TableCell>
                      <TableCell>{liquidacion.SrvDl_FechaFinalizacion && moment(liquidacion.SrvDl_FechaFinalizacion).format('YYYY-MM-DD')}</TableCell>
                      <TableCell>
                        {loadingEstado === liquidacion.SrvDI_Id ? (
                          <CircularProgress size={24} />
                        ) : (
                          <Select disabled={Prm_Liquid_Camb_Estado ===0} value={liquidacion.SrvDI_Estado.toString()} style={{ backgroundColor: estadoLiquidacion.find((estado) => estado.id === parseInt(liquidacion.SrvDI_Estado))?.color }} onChange={(e) => handleEstadoChange(liquidacion.SrvDI_Id, e.target.value as string)} fullWidth size='small'>
                            {estadoLiquidacion.map((estado) => (
                              <MenuItem key={estado.id} value={estado.id.toString()}>
                                {estado.texto}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      </TableCell>
                      <TableCell>{liquidacion.SrvDI_FechaLiquidacion && moment(liquidacion.SrvDI_FechaLiquidacion).format('YYYY-MM-DD')}</TableCell>
                      <TableCell>{liquidacion.Srv_VehiculoPlacaOSerial}</TableCell>
                      <TableCell>{liquidacion.SrvDI_Detalle}</TableCell>
                      <TableCell>{liquidacion.SrvDI_Valor}</TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => {
                            setSelectedLiquidacion(liquidacion);
                            setOpenView(true);
                          }}>
                          <Visibility color='info' />
                        </IconButton>
                        {Prm_Liquid_Eliminar === 1 && (
                          <IconButton onClick={() => handleOpenDeleteConfirm(liquidacion.SrvDI_Id)} title='Eliminar'>
                            <Delete color='error' />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Fila para el total */}
                  <TableRow>
                    <TableCell colSpan={5} align='right'>
                      <strong>Total:</strong>
                    </TableCell>
                    <TableCell>
                      <strong>{calculateTotalValue(filteredLiquidaciones)}</strong>
                    </TableCell>
                    <TableCell colSpan={3} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component='div' count={filteredLiquidaciones.length} page={page} onPageChange={handleChangePage} rowsPerPage={rowsPerPage} onRowsPerPageChange={handleChangeRowsPerPage} />
          </>
        )}
      </div>

      <Dialog open={openEdit} onClose={() => setOpenEdit(false)}>
        <DialogTitle>Editar liquidacion</DialogTitle>
        <DialogContent>
          <EditLiquidacion
            liquidacion={selectedLiquidacion}
            onClose={() => {
              setOpenEdit(false);
              fetchLiquidaciones();
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={openView} onClose={() => setOpenView(false)}>
        <DialogTitle>Ver liquidacion</DialogTitle>
        <DialogContent>
          <ViewLiquidacion liquidacion={selectedLiquidacion} onClose={() => setOpenView(false)} />
        </DialogContent>
      </Dialog>

      <ConfirmDelete open={openConfirmDelete} onClose={handleCloseDeleteConfirm} onConfirm={handleConfirmDelete} message='¿Estás seguro de que deseas eliminar esta liquidacion?' />
    </div>
  );
};

export default Liquidaciones;
